import * as Amplitude from '@amplitude/analytics-browser'
import { config } from 'config'
import { useRef } from 'react'

// Returning null resolves the event with status code 0 and an empty message.
// The SDK logs that as an error. The drop is intentional, so ignore only that case.
const developmentLogger: Amplitude.Types.ILogger = {
  disable: () => undefined,
  enable: () => undefined,
  log: () => undefined,
  debug: () => undefined,
  warn: (...args: unknown[]) => {
    // eslint-disable-next-line no-console
    console.warn(`Amplitude Logger [Warn]: ${args.join(' ')}`)
  },
  error: (...args: unknown[]) => {
    const message = args.join(' ')
    if (message.length === 0) {
      return
    }
    // eslint-disable-next-line no-console
    console.error(`Amplitude Logger [Error]: ${message}`)
  },
}

export const initAmplitude = () => {
  if (config.env === 'development') {
    Amplitude.add({
      name: 'dev-console-logger',
      type: 'enrichment',
      execute: async (event) => {
        // eslint-disable-next-line no-console
        console.info('[Amplitude]', event.event_type, event)
        return null // drop event so nothing is sent
      },
    })
  }

  Amplitude.init(config.amplitudeApiKey, undefined, {
    loggerProvider:
      config.env === 'development' ? developmentLogger : undefined,
    autocapture: {
      attribution: true,
      elementInteractions: true,
      fileDownloads: true,
      formInteractions: true,
      frustrationInteractions: false,
      networkTracking: false,
      pageUrlEnrichment: true,
      pageViews: true,
      sessions: true,
      webVitals: false,
    },
  })
}

export const setAmplitudeUserDevice = (installationToken: string) => {
  return Amplitude.setDeviceId(installationToken)
}

export const setAmplitudeUserId = (userId?: string) => {
  return Amplitude.setUserId(userId)
}

// Call `set` per user property on the identify instance
export const useAmplitudeIdentify = (): Amplitude.Identify => {
  const identifyRef = useRef(new Amplitude.Identify())
  return identifyRef.current
}

// Once all properties are set, submit
export const identify = (identifyObj: Amplitude.Identify) => {
  return Amplitude.identify(identifyObj)
}

// e.g. on Logout
export const reset = () => {
  return Amplitude.reset()
}

export const setSessionId = (sessionId: number) => {
  return Amplitude.setSessionId(sessionId)
}

export const track = (
  eventType: string,
  eventProperties?: {
    [key: string]: any
  },
  groups?: {
    [key: string]: any
  },
  groupProperties?: {
    [key: string]: any
  }
): ReturnType<typeof Amplitude.track> => {
  return Amplitude.track({
    event_type: eventType,
    event_properties: eventProperties,
    groups,
    group_properties: groupProperties,
  }) as ReturnType<typeof Amplitude.track>
}
